/* NutriPaw sections: one shared, idempotent runtime. No cart interception. */
(() => {
  if (window.NutriPawHomeV2) return;
  const active = new Map();
  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const behaviour = () => reduced() ? 'auto' : 'smooth';

  function carousel(root, signal, disposers) {
    const track = root.querySelector('[data-np2-track]');
    if (!track) return;
    const prev = root.querySelector('[data-np2-prev]');
    const next = root.querySelector('[data-np2-next]');
    const controls = root.querySelector('[data-np2-controls]');
    const dots = root.querySelector('[data-np2-dots]');
    let points = [], raf = 0;
    const rtl = () => getComputedStyle(track).direction === 'rtl';
    const position = () => Math.abs(track.scrollLeft);
    const scrollTo = value => track.scrollTo({left: rtl() ? -value : value, behavior: behaviour()});
    const current = () => points.reduce((best, point, index) => Math.abs(point - position()) < Math.abs(points[best] - position()) ? index : best, 0);
    function update() {
      raf = 0;
      const max = Math.max(0, track.scrollWidth - track.clientWidth);
      if (prev) prev.disabled = position() < 2;
      if (next) next.disabled = position() >= max - 2;
      if (controls) controls.hidden = max < 2;
      if (dots) {
        dots.hidden = max < 2;
        [...dots.children].forEach((dot, i) => dot.setAttribute('aria-current', String(i === current())));
      }
    }
    function measure() {
      const max = Math.max(0, track.scrollWidth - track.clientWidth);
      const trackRect = track.getBoundingClientRect();
      const startPadding = parseFloat(getComputedStyle(track).paddingInlineStart) || 0;
      points = [...track.children].map(card => {
        const r = card.getBoundingClientRect();
        const offset = rtl() ? trackRect.right - r.right : r.left - trackRect.left;
        return Math.min(max, Math.max(0, Math.round(offset + position() - startPadding)));
      }).filter((point, index, all) => index === 0 || Math.abs(point - all[index - 1]) > 2);
      if (!points.length) points = [0];
      if (max > 2 && Math.abs(points[points.length - 1] - max) > 2) points.push(max);
      if (dots && dots.childElementCount !== points.length) {
        const fragment = document.createDocumentFragment();
        points.forEach((_, index) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.setAttribute('aria-label', `${dots.dataset.label || 'Go to item'} ${index + 1}`);
          button.dataset.point = String(index);
          fragment.append(button);
        });
        dots.replaceChildren(fragment);
      }
      update();
    }
    function step(direction) {
      const pos = position();
      const target = direction > 0 ? points.find(p => p > pos + 2) : [...points].reverse().find(p => p < pos - 2);
      if (target !== undefined) scrollTo(target);
    }
    prev?.addEventListener('click', () => step(-1), {signal});
    next?.addEventListener('click', () => step(1), {signal});
    dots?.addEventListener('click', e => {
      const dot = e.target.closest('button[data-point]');
      if (dot) scrollTo(points[Number(dot.dataset.point)]);
    }, {signal});
    track.addEventListener('scroll', () => {if (!raf) raf = requestAnimationFrame(update);}, {passive:true, signal});
    track.addEventListener('keydown', e => {
      if (e.target !== track || !['ArrowRight','ArrowLeft','Home','End'].includes(e.key)) return;
      e.preventDefault();
      if (e.key === 'Home') scrollTo(0);
      else if (e.key === 'End') scrollTo(points[points.length - 1]);
      else step((e.key === 'ArrowRight' ? 1 : -1) * (rtl() ? -1 : 1));
    }, {signal});
    const observer = new ResizeObserver(measure);
    observer.observe(track);
    [...track.children].forEach(card => observer.observe(card));
    disposers.push(() => {observer.disconnect(); cancelAnimationFrame(raf);});
    measure();
  }

  function mount(root) {
    if (active.has(root)) return;
    const controller = new AbortController(), disposers = [];
    active.set(root, () => {controller.abort(); disposers.forEach(fn => fn());});
    const {signal} = controller;
    root.querySelectorAll('[data-np2-carousel]').forEach(el => carousel(el, signal, disposers));
    root.querySelectorAll('details').forEach(details => {
      details.addEventListener('toggle', () => {
        if (!details.open || root.dataset.single !== 'true') return;
        root.querySelectorAll('details[open]').forEach(other => {if (other !== details) other.open = false;});
      }, {signal});
    });
    const pause = root.querySelector('[data-np2-pause]');
    pause?.addEventListener('click', () => {
      const paused = pause.getAttribute('aria-pressed') !== 'true';
      pause.setAttribute('aria-pressed', String(paused));
      pause.setAttribute('aria-label', paused ? pause.dataset.play : pause.dataset.pause);
      pause.textContent = paused ? '▶' : 'Ⅱ';
      root.classList.toggle('np2-is-paused', paused);
    }, {signal});
  }
  function init(scope = document) {
    if (scope.matches?.('.np2')) mount(scope);
    scope.querySelectorAll('.np2').forEach(mount);
  }
  function unmount(scope) {
    active.forEach((dispose, root) => {
      if (root === scope || scope.contains(root)) {dispose(); active.delete(root);}
    });
  }
  document.addEventListener('shopify:section:load', e => init(e.target));
  document.addEventListener('shopify:section:unload', e => unmount(e.target));
  document.addEventListener('shopify:block:select', e => {
    const block = e.target;
    if (!block.closest('.np2')) return;
    const details = block.matches('details') ? block : block.closest('details');
    if (details) details.open = true;
    const track = block.closest('[data-np2-track]');
    if (track) {
      const t = track.getBoundingClientRect(), b = block.getBoundingClientRect();
      track.scrollBy({left: b.left - t.left, behavior: 'auto'});
    }
  });
  document.addEventListener('shopify:section:select', e => {
    e.target.querySelectorAll('[data-np2-marquee]').forEach(root => root.classList.add('np2-editor-paused'));
  });
  document.addEventListener('shopify:section:deselect', e => {
    e.target.querySelectorAll('[data-np2-marquee]').forEach(root => root.classList.remove('np2-editor-paused'));
  });
  window.NutriPawHomeV2 = {init};
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), {once:true});
  else init();
})();
